import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import shortId from 'shortid';
import classnames from 'classnames';

import { getScale } from 'utils';
import { showStats } from 'actions/gameActions';
import * as buttonsTypes from 'consts/buttons';
import { playWatch } from 'actions/audioActions';
import { animations, getInstruction } from 'activity-templates/utils';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import { stringProblemsMapper } from 'activity-templates/utils';
import { prepareProblem, validateProblem, validateItem } from './findTheOneHelpers';
import {
  ActivityButtons,
  GameModal,
  Instructions,
  ProblemsProgress,
  MultiplayerOverlay,
} from 'components/flink-play';
import {
  ItemsDragLayer,
  ItemSlots,
  BoardWithDraggableItems,
} from './draggable-components';
import classes from './FindTheOne.module.scss';
import { imagesURL, s3bucketPublicURL } from 'config';
import MobileFindTheOneFeedback from './mobile/MobileFindTheOneFeedback';
import './mobile/FindTheOne.mobile.scss';
import {
  playMobileFindTheOneStandardFeedback,
  playMobileFindTheOneAudio,
  playMobileFindTheOneWatch,
  toggleMobileFindTheOneAudio,
  stopMobileFindTheOneAudio,
} from './mobile/mobileFindTheOneAudio';

import {
  changePlayerTurn,
  incrementPlayerPoints,
} from 'actions/gameActions';

const animationSpeed = 1000;
const NATIVE_PHONE_MAX_SCALE = 0.85;
const NATIVE_PHONE_WORK_WIDTH = 900;
const NATIVE_PHONE_WORK_HEIGHT = 360;
const NATIVE_PHONE_HORIZONTAL_INSET = 24;
const NATIVE_PHONE_GLOBAL_BAR_RESERVE = 72;

const getNativePhoneScale = () => {
  if (typeof window === 'undefined') return NATIVE_PHONE_MAX_SCALE;

  const widths = [
    window.visualViewport?.width,
    document.documentElement?.clientWidth,
    window.innerWidth,
  ].filter(value => Number(value) > 0);
  const heights = [
    window.visualViewport?.height,
    document.documentElement?.clientHeight,
    window.innerHeight,
  ].filter(value => Number(value) > 0);
  const viewportWidth = widths.length ? Math.min(...widths) : 0;
  const viewportHeight = heights.length ? Math.min(...heights) : 0;

  if (!viewportWidth || !viewportHeight) return NATIVE_PHONE_MAX_SCALE;

  const widthScale = (
    viewportWidth - NATIVE_PHONE_HORIZONTAL_INSET
  ) / NATIVE_PHONE_WORK_WIDTH;
  const heightScale = (
    viewportHeight - NATIVE_PHONE_GLOBAL_BAR_RESERVE
  ) / NATIVE_PHONE_WORK_HEIGHT;

  return Math.min(NATIVE_PHONE_MAX_SCALE, widthScale, heightScale);
};

const isNativePhoneViewport = (mobileLayout = false) => {
  if (!mobileLayout || typeof window === 'undefined') return false;

  const shortSide = Math.min(window.innerWidth || 0, window.innerHeight || 0);
  return shortSide > 0 && shortSide <= 700;
};

let boxesCount = 0;

const getItems = (problem) => {
  if (!problem) return null;

  const {
    questions,
    correctAnswers,
    incorrectAnswers,
    incorrectItemsShown = 2,
  } = problem;

  const validCorrectAnswers = correctAnswers.filter(validateItem);
  const validIncorrectAnswers = incorrectAnswers.filter(validateItem);
  const validQuestions = questions.filter(validateItem);

  const answersTogether = validIncorrectAnswers.length + 1;
  boxesCount = +incorrectItemsShown + 1;
  boxesCount = boxesCount > answersTogether ? answersTogether : boxesCount;

  const startPositions = _.chain(boxesCount).times().shuffle().value();

  const randomQuestionItem = _.shuffle(validQuestions)[0];
  const randomCorrectItem = _.shuffle(validCorrectAnswers)[0];
  const randomIncorrectItems = _.chain(validIncorrectAnswers)
    .shuffle()
    .slice(0, incorrectItemsShown)
    .value();

  const questionItem = {
    id: shortId.generate(),
    item: randomQuestionItem,
    inSlot: true,
    fixed: true,
    slotIdx: 0,
    isCorrect: true,
  };

  const correctItem = {
    id: shortId.generate(),
    item: randomCorrectItem,
    inSlot: false,
    fixed: false,
    boxIdx: _.last(startPositions),
    isCorrect: true,
  };

  const incorrectItems = randomIncorrectItems.map((item, idx) => ({
    id: shortId.generate(),
    item,
    inSlot: false,
    fixed: false,
    boxIdx: startPositions[idx],
    isCorrect: false,
  }));

  const allItems = _.concat(incorrectItems, correctItem, questionItem);
  return allItems;
};

class FindTheOne extends Component {
  constructor(props) {
    super(props);

    const { options } = props.activity.data;
    const { translate, getStringAudio } = props;

    const locale = props.activity.activity?.monolingual
      ? ''
      : (
          window.location.href.indexOf('edit-activities') !== -1 &&
          props.activity.activity?.currentLanguageVersion?.locale
        )
        ? props.activity.activity.currentLanguageVersion.locale
        : props?.solutionLocale?.code
          ? props.solutionLocale.code
          : '';

    const requestedVersionType = (!locale || locale === 'en')
      ? ''
      : `immersion_${locale}`;

    const localizedGameData = requestedVersionType
      ? props.activity.data.languageVersionData?.[requestedVersionType]
      : null;

    const hasLocalizedGameData = !!localizedGameData;
    const versionType = hasLocalizedGameData ? requestedVersionType : '';
    const gameData = localizedGameData || props.activity.data.gameData || {};

    console.log('[FindTheOne][resolveGameData]', {
      locale,
      requestedVersionType,
      effectiveVersionType: versionType,
      hasLocalizedGameData,
      hasDefaultGameData: !!props.activity.data.gameData,
      localizedProblemsCount: Array.isArray(localizedGameData?.problems)
        ? localizedGameData.problems.length
        : 0,
      defaultProblemsCount: Array.isArray(props.activity.data.gameData?.problems)
        ? props.activity.data.gameData.problems.length
        : 0,
    });

    const mapper = stringProblemsMapper({ translate, getStringAudio });

    let problems = Array.isArray(gameData.problems)
      ? gameData.problems
          .filter(validateProblem)
          .map(prepareProblem(mapper))
      : [];

    const { questionsLimit, randomOrder } = options;

    problems = randomOrder ? _.shuffle(problems) : problems;

    if (
      questionsLimit &&
      questionsLimit > 0 &&
      questionsLimit < problems.length
    ) {
      problems = _.take(problems, questionsLimit);
    }

    const maxTextLength = _.chain(problems)
      .map((p) => [...p.questions, ...p.correctAnswers, ...p.incorrectAnswers])
      .flatten()
      .map((i) => i && i.text && i.text.length)
      .max()
      .value();

    this.state = {
      problems,
      maxTextLength,
      currentProblem: null,
      versionType,
      hasLocalizedGameData,
      instructionModalOpen: false,
      mobileFeedback: null,
      mobileFeedbackAudioSrc: '',
    };
  }

  componentDidMount() {
    document.addEventListener('startGame', this.startGame);
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutBeforeNext);
    document.removeEventListener('audioEnded', this.setupNextQuestion);
    document.removeEventListener('startGame', this.startGame);
    stopMobileFindTheOneAudio();
  }

  startGame = () => {
    const { problems } = this.state;
    const currentProblem = problems[0];
    const items = getItems(currentProblem);

    this.refs = {
      boxes: _.times(boxesCount).map(() => React.createRef()),
      slots: _.times(2).map(() => React.createRef()),
    };

    this.setState({
      currentProblem,
      items,
      animations: _.shuffle(animations),
      currentIndex: 0,
      isResolved: false,
      problemsCount: problems.length,
      attemptsOnCurrentProblem: 0,
      answeredCorrectly: 0,
      answeredIncorrectly: 0,
      problemsLeft: problems.length,
      instructionModalOpen: false,
      mobileFeedback: null,
      mobileFeedbackAudioSrc: '',
    });
  };

  openInstructionModal = () => {
    this.setState({ instructionModalOpen: true });
  };

  closeInstructionModal = () => {
    this.setState({ instructionModalOpen: false });
  };

  onDrop = ({ itemId, slotIdx }) => {
    if (slotIdx !== undefined) {
      this.moveToSlot(itemId, slotIdx);
    } else {
      this.moveToBoard(itemId);
    }
  };

  moveToSlot = (itemId, slotIdx) => {
    const { items } = this.state;

    const updatedItems = items.map((item) => {
      if (item.id === itemId) {
        return { ...item, slotIdx, inSlot: true };
      }

      if (item.slotIdx === slotIdx) {
        return { ...item, slotIdx: undefined, inSlot: false };
      }

      return item;
    });

    this.setState({ items: updatedItems });

    if (this.props.mobileLayout) {
      // Native touch drops may batch React state. Validate the exact items from
      // this drop while the iOS/Android user gesture is still active.
      this.checkAnswer(updatedItems);
      return;
    }

    // Keep the existing web flow unchanged.
    this.checkAnswer();
  };

  moveToBoard = (itemId) => {
    const { items } = this.state;

    this.setState({
      items: items.map((item) => {
        if (item.id !== itemId) return item;
        return { ...item, inSlot: false, slotIdx: null };
      }),
    });
  };

  checkAnswer = (itemsOverride = null) => {
    const {
      items: stateItems,
      currentProblem,
      attemptsOnCurrentProblem,
    } = this.state;
    const items = Array.isArray(itemsOverride) ? itemsOverride : stateItems;
    const {
      changePlayerTurn,
      incrementPlayerPoints,
      multiplayerModeEnabled,
    } = this.props;

    const itemInSlot = _.find(items, { slotIdx: 1 });
    if (!itemInSlot) return;

    const feedback = { isCorrect: itemInSlot.isCorrect };

    if (currentProblem.specificFeedback) {
      feedback.audio = itemInSlot.item.feedbackAudio;
      feedback.text = itemInSlot.item.feedbackText;
    }

    if (itemInSlot.isCorrect) {
      this.setState({ isResolved: true });

      if (multiplayerModeEnabled) {
        incrementPlayerPoints(attemptsOnCurrentProblem);
      }

      feedback.onClose = () => {
        this.setupNextQuestion(() => {
          if (multiplayerModeEnabled) {
            changePlayerTurn();
          }
        });
      };
    } else {
      if (multiplayerModeEnabled) {
        changePlayerTurn();
      }

      this.setState((state) => ({
        attemptsOnCurrentProblem: state.attemptsOnCurrentProblem + 1,
        answeredIncorrectly:
          state.attemptsOnCurrentProblem === 0
            ? state.answeredIncorrectly + 1
            : state.answeredIncorrectly,
      }));
    }

    if (this.props.mobileLayout) {
      this.showMobileFeedback(feedback);
      return;
    }

    this.props.showFeedback(feedback);
  };

  buildMobileFeedbackAudioSrc = (feedback) => {
    if (!feedback?.audio) return '';

    const contentFolder = this.props.activity?.activity?.contentFolder;
    if (!contentFolder) return '';

    const { versionType } = this.state;
    const contentPath = versionType
      ? `${s3bucketPublicURL}/${contentFolder}/languageVersionData/${versionType}/`
      : `${s3bucketPublicURL}/${contentFolder}/gamedata/`;

    return contentPath + feedback.audio;
  };

  finishMobileFeedback = (feedback, { stopAudio = false } = {}) => {
    if (!feedback || this.activeMobileFeedback !== feedback) return;

    this.activeMobileFeedback = null;

    if (stopAudio) {
      stopMobileFindTheOneAudio();
    }

    this.setState({
      mobileFeedback: null,
      mobileFeedbackAudioSrc: '',
    });

    if (typeof feedback.onClose === 'function') {
      feedback.onClose();
    }
  };

  showMobileFeedback = (feedback) => {
    stopMobileFindTheOneAudio();
    this.activeMobileFeedback = feedback;

    const isCustomFeedback = !!(feedback.text || feedback.audio);
    const feedbackAudioSrc = this.buildMobileFeedbackAudioSrc(feedback);

    if (isCustomFeedback) {
      this.setState({
        mobileFeedback: feedback,
        mobileFeedbackAudioSrc: feedbackAudioSrc,
      });
    }

    const onAudioEnd = () => {
      this.finishMobileFeedback(feedback);
    };

    console.log('[FindTheOne][mobileFeedback]', {
      isCorrect: feedback.isCorrect,
      isCustomFeedback,
      hasText: !!feedback.text,
      audioSrc: feedbackAudioSrc || 'standard-feedback',
    });

    if (feedbackAudioSrc) {
      playMobileFindTheOneAudio(feedbackAudioSrc, { onEnd: onAudioEnd });
      return;
    }

    if (!isCustomFeedback) {
      playMobileFindTheOneStandardFeedback(feedback.isCorrect, {
        onEnd: onAudioEnd,
      });
      return;
    }

    // Custom text-only feedback follows the existing GameFeedback behavior:
    // it stays open until the learner closes it manually.
    if (!feedback.text) {
      this.finishMobileFeedback(feedback);
    }
  };

  closeMobileFeedback = () => {
    const feedback = this.activeMobileFeedback || this.state.mobileFeedback;
    this.finishMobileFeedback(feedback, { stopAudio: true });
  };

  finishGame = () => {
    const { answeredCorrectly, answeredIncorrectly, problems } = this.state;

    this.props.showStats({
      withScore: true,
      data: {
        allProblemsCount: problems.length,
        problemsAnsweredCorrectly: answeredCorrectly,
        problemsAnsweredIncorrectly: answeredIncorrectly,
      },
    });

    this.setState({
      currentProblem: null,
      instructionModalOpen: false,
    });
  };

  setupNextQuestion = (callback) => {
    const {
      problemsLeft,
      answeredCorrectly,
      attemptsOnCurrentProblem,
    } = this.state;

    const { delayBeforeNext } = this.props.activity.data.options;

    let newAnsweredCorrectly = answeredCorrectly;

    if (!attemptsOnCurrentProblem) {
      newAnsweredCorrectly++;
    }

    const newProblemsLeft = problemsLeft - 1;

    if (!newProblemsLeft) {
      const finalState = {
        answeredCorrectly: newAnsweredCorrectly,
        problemsLeft: newProblemsLeft,
      };

      if (isMobileAppRuntime()) {
        this.setState(finalState, () => {
          this.timeoutBeforeNext = setTimeout(() => {
            this.finishGame();
          }, delayBeforeNext * 1000);
        });

        return;
      }

      this.setState(finalState);

      this.timeoutBeforeNext = setTimeout(() => {
        this.finishGame();
      }, delayBeforeNext * 1000);

      return;
    }

    this.timeoutBeforeNext = setTimeout(() => {
      this.setState((state) => {
        const nextProblem = state.problems[state.currentIndex + 1];
        const items = getItems(nextProblem);

        this.refs.slots = _.times(2).map(() => React.createRef());

        return {
          items,
          answeredCorrectly: newAnsweredCorrectly,
          attemptsOnCurrentProblem: 0,
          problemsLeft: newProblemsLeft,
          isResolved: false,
          currentProblem: nextProblem,
          currentIndex: state.currentIndex + 1,
          instructionModalOpen: false,
        };
      });

      if (typeof callback === 'function') callback();
    }, delayBeforeNext * 1000);
  };

  showAnswer = () => {
    const { items } = this.state;
    const { multiplayerModeEnabled, mobileLayout } = this.props;

    const changedItems = [...items];
    const correctItem = _.find(items, { isCorrect: true, inSlot: false });

    if (!correctItem) return;

    correctItem.moveFrom = this.refs.boxes[correctItem.boxIdx].current;
    correctItem.inSlot = true;
    correctItem.slotIdx = 1;

    changedItems.forEach((i) => {
      if (!i.isCorrect && i.inSlot) {
        i.moveFrom = this.refs.slots[i.slotIdx].current;
        i.inSlot = false;
        i.slotIdx = null;
      }
    });

    this.setState((state) => ({
      items: [...changedItems],
      isResolved: true,
      attemptsOnCurrentProblem: state.attemptsOnCurrentProblem + 1,
    }));

    if (multiplayerModeEnabled) this.checkAnswer(changedItems);

    if (mobileLayout) {
      // Mobile must not depend on the global audioEnded event. On iOS that event
      // can be lost when the first audio is started from the Correct Answer click.
      // The isolated FindTheOne player owns this playback and always completes
      // through its onEnd/error/watchdog path.
      playMobileFindTheOneWatch({
        onEnd: () => this.setupNextQuestion(),
      });
      return;
    }

    // Preserve the existing desktop/web path exactly.
    document.addEventListener('audioEnded', this.setupNextQuestion, {
      once: true,
    });
    this.props.playWatch();
  };

  render() {
    const {
      solutionLocale,
      translate,
      getStringAudio,
      activity,
      mobileLayout = false,
    } = this.props;
    const {
      problems,
      isResolved,
      problemsLeft,
      maxTextLength,
      currentProblem,
      attemptsOnCurrentProblem,
      hasLocalizedGameData,
      versionType,
      instructionModalOpen,
    } = this.state;

    const instructionLocale = hasLocalizedGameData ? solutionLocale : null;
    const instructionLocaleCode = hasLocalizedGameData
      ? (solutionLocale?.code || '')
      : 'en';

    const instruction = getInstruction(
      currentProblem,
      instructionLocale,
      undefined,
      undefined,
      undefined,
      { translate, getStringAudio },
    );

    console.log('[FindTheOne][instructionAssetResolve]', {
      versionType,
      hasLocalizedGameData,
      requestedLocale: solutionLocale?.code || '',
      effectiveLocale: instructionLocale?.code || '',
      instructionAudio: instruction?.audio || '',
      instructionImage: instruction?.image || '',
    });

    const { multiplayerModeEnabled } = this.props;
    const { options } = activity.data;
    const textStyle = currentProblem
      ? {
          fontFamily: currentProblem.fontFamily,
          color: currentProblem.fontColor,
        }
      : {};

    return (
      <>
        <div
          style={textStyle}
          className={classnames(classes.text, {
            [classes.textUpTo1]: maxTextLength === 1,
            [classes.textUpTo2]: maxTextLength === 2,
            [classes.textUpTo4]: maxTextLength > 2 && maxTextLength <= 4,
            [classes.textUpTo6]: maxTextLength > 4 && maxTextLength <= 6,
            [classes.textUpTo10]: maxTextLength > 6 && maxTextLength <= 10,
          })}
        >
          {mobileLayout ? (
            <div className={classes.mobileInstructionAudioBridge} aria-hidden="true">
              <Instructions
                problem={currentProblem}
                solutionLocaleOverride={instructionLocale}
                locale={instructionLocaleCode}
                activityAudioBaseUrl={this.props.contentFolder}
              />
            </div>
          ) : (
            <Instructions
              problem={currentProblem}
              solutionLocaleOverride={instructionLocale}
              locale={instructionLocaleCode}
              activityAudioBaseUrl={this.props.contentFolder}
            />
          )}

          {this.renderBlocks()}

          {problems && (
            <ProblemsProgress
              problemsNumber={problems.length}
              problemsLeft={problemsLeft}
            />
          )}
        </div>

        {multiplayerModeEnabled && (
          <MultiplayerOverlay />
        )}

        {mobileLayout && (
          <MobileFindTheOneFeedback
            feedback={this.state.mobileFeedback}
            audioSrc={this.state.mobileFeedbackAudioSrc}
            onToggleAudio={toggleMobileFindTheOneAudio}
            onClose={this.closeMobileFeedback}
          />
        )}

        {mobileLayout && instructionModalOpen && instruction.text && (
          <GameModal
            title={translate(432, 'Instructions')}
            outerClassnames={[classes.mobileInstructionModal]}
            innerClassnames={[classes.mobileInstructionModalInner]}
            contentClassnames={[classes.mobileInstructionModalContent]}
            titleClassnames={[classes.mobileInstructionModalTitle]}
            buttonsClassnames={[classes.mobileInstructionModalButtons]}
            buttons={[
              {
                icon: `${imagesURL}/Images/Help/Close50.png`,
                clickHandler: this.closeInstructionModal,
              },
            ]}
          >
            <div className={classes.mobileInstructionText}>
              {instruction.text}
            </div>
          </GameModal>
        )}

        <ActivityButtons
          buttons={[
            buttonsTypes.GO_BACK,
            {
              type: buttonsTypes.ACTIVITY_IMAGE,
              onClick: this.openInstructionModal,
              dontShow: !mobileLayout || !instruction.text,
              titleStringNumber: 432,
              titleName: 'Instructions',
            },
            {
              type: buttonsTypes.CORRECT_ANSWER,
              onClick: () => this.showAnswer(),
              dontShow:
                isResolved ||
                options.showAnswer === 'n/a' ||
                +options.showAnswer > attemptsOnCurrentProblem,
            },
            {
              type: buttonsTypes.SPEAK,
              dontShow: !instruction.audio || isResolved,
            },
            buttonsTypes.HELP,
          ]}
        />
      </>
    );
  }

  renderBlocks = () => {
    const {
      items,
      isResolved,
      animations,
      currentIndex,
      currentProblem,
    } = this.state;

    if (!currentProblem || !items) return null;

    const {
      activity: {
        data: { options },
      },
      mobileLayout = false,
    } = this.props;
    const nativePhoneViewport = isNativePhoneViewport(mobileLayout);
    const activityScale = nativePhoneViewport
      ? getNativePhoneScale()
      : getScale();

    return (
      <>
        <ItemsDragLayer
          style={{
            transform: 'scale(' + activityScale + ')',
          }}
        />

        <div
          className={classnames(
            classes.boxesWrapper,
            mobileLayout && 'find-the-one-mobile-work-area'
          )}
          style={{
            width: 900,
            top: nativePhoneViewport ? '45%' : '50%',
            transform: 'translate(-50%, -50%) scale(' + activityScale + ')',
          }}
        >
          <BoardWithDraggableItems
            isResolved={isResolved}
            boxesCount={boxesCount}
            onDrop={this.onDrop}
            items={items}
            refs={this.refs.boxes}
          />

          <ItemSlots
            slotClassname={classnames(classes.slot, {
              [classes.nativePhoneSlot]: nativePhoneViewport,
            })}
            fixedSlotClassname={classes.fixedSlot}
            options={options}
            animationSpeed={animationSpeed}
            isResolved={isResolved}
            preserveResolvedContent={mobileLayout}
            animation={animations[currentIndex % animations.length]}
            count={2}
            onDrop={this.onDrop}
            items={items}
            refs={this.refs.slots}
          />
        </div>
      </>
    );
  };
}

export default connect(null, {
  playWatch,
  showStats,
  changePlayerTurn,
  incrementPlayerPoints,
})(FindTheOne);
